import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type Report = {
  id: string;
  reporterId: string;
  targetType: string;
  targetId: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string | null;
};

export default function AdminReports() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: reports, isLoading } = useQuery<Report[]>({
    queryKey: ["/api/admin/reports"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const resolve = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "actioned" | "dismissed" }) => {
      await apiRequest("POST", `/api/admin/reports/${id}/resolve`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reports"] });
      toast({ title: "Report resolved" });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't resolve", description: e?.message, variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={() => setLocation("/account")}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Moderation queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Open content reports. Review and action within 24 hours.
        </p>

        {isLoading && (
          <div className="flex items-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!isLoading && (!reports || reports.length === 0) && (
          <p className="py-10 text-sm text-muted-foreground">No open reports. 🎉</p>
        )}

        <div className="mt-4 space-y-3">
          {reports?.map((r) => (
            <div key={r.id} className="rounded-xl border p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold capitalize">
                  {r.targetType} · {r.reason}
                </span>
                <span className="text-xs text-muted-foreground">
                  {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                </span>
              </div>
              {r.details && <p className="mt-1 text-sm text-muted-foreground">{r.details}</p>}
              <p className="mt-1 break-all text-xs text-muted-foreground">
                target id: {r.targetId} · reporter: {r.reporterId}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => resolve.mutate({ id: r.id, status: "actioned" })}
                  disabled={resolve.isPending}
                >
                  Mark actioned
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resolve.mutate({ id: r.id, status: "dismissed" })}
                  disabled={resolve.isPending}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
